import React from "react";
import ImageUploadField from "./ImageUploadField";
import { OfficeFooter } from "../PDFExportConfigModal";

interface OfficeFooterManagementProps {
	officeFooters: OfficeFooter[];
	availableOffices: string[];
	organizationId: string;
	onOfficeFootersChange: (officeFooters: OfficeFooter[]) => void;
}

const OfficeFooterManagement: React.FC<OfficeFooterManagementProps> = ({
	officeFooters,
	availableOffices,
	organizationId,
	onOfficeFootersChange,
}) => {
	const updateEntry = (index: number, updates: Partial<OfficeFooter>) => {
		const updated = [...officeFooters];
		updated[index] = { ...updated[index], ...updates };
		onOfficeFootersChange(updated);
	};

	const addEntry = () => {
		onOfficeFootersChange([...officeFooters, { officeNames: [], footerLines: [] }]);
	};

	const removeEntry = (index: number) => {
		onOfficeFootersChange(officeFooters.filter((_, i) => i !== index));
	};

	const toggleOffice = (index: number, office: string) => {
		const officeNames = officeFooters[index].officeNames;
		updateEntry(index, {
			officeNames: officeNames.includes(office) ? officeNames.filter((name) => name !== office) : [...officeNames, office],
		});
	};

	const updateLine = (index: number, lineIndex: number, value: string) => {
		const footerLines = [...(officeFooters[index].footerLines ?? [])];
		footerLines[lineIndex] = value;
		updateEntry(index, { footerLines });
	};

	// The backend uses the first entry that lists an office, so an office can only belong to one entry.
	const getOwningEntryIndex = (office: string) => officeFooters.findIndex((entry) => entry.officeNames.includes(office));

	return (
		<div className="p-4 bg-white rounded-lg border border-gray-200 space-y-4">
			<div className="flex justify-between items-center">
				<div className="text-gray-900 text-base font-medium font-['Hanken Grotesk']">Office Footers (optional)</div>
				<button
					type="button"
					onClick={addEntry}
					disabled={availableOffices.length === 0}
					className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					+ Add Office Footer
				</button>
			</div>
			<div className="text-gray-500 text-xs">
				Override the default footer lines and/or footer image for specific offices. Anything left empty here uses the default.
			</div>

			{availableOffices.length === 0 ? (
				<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500 text-sm">
					No offices available. Configure offices in organization settings to assign office-specific footers.
				</div>
			) : officeFooters.length === 0 ? (
				<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500 text-sm">
					No office footers configured. All offices use the default footer.
				</div>
			) : (
				officeFooters.map((entry, index) => {
					const footerLines = entry.footerLines ?? [];
					return (
						<div key={index} className="p-4 bg-white rounded-lg border border-gray-200 space-y-4">
							<div className="flex justify-between items-center">
								<div className="text-gray-700 text-sm font-medium">Office Footer #{index + 1}</div>
								<button
									type="button"
									onClick={() => removeEntry(index)}
									className="px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
								>
									Remove
								</button>
							</div>

							<div>
								<label className="block text-gray-700 text-sm font-medium mb-2">Assign to Offices</label>
								<div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded">
									{availableOffices.map((office) => {
										const owningEntryIndex = getOwningEntryIndex(office);
										const isTakenByOtherEntry = owningEntryIndex !== -1 && owningEntryIndex !== index;
										return (
											<label
												key={office}
												className={`flex items-center space-x-2 p-1 rounded ${
													isTakenByOtherEntry
														? "opacity-50 cursor-not-allowed"
														: "cursor-pointer hover:bg-gray-50"
												}`}
												title={
													isTakenByOtherEntry
														? `Already assigned to Office Footer #${owningEntryIndex + 1}`
														: undefined
												}
											>
												<input
													type="checkbox"
													checked={entry.officeNames.includes(office)}
													disabled={isTakenByOtherEntry}
													onChange={() => toggleOffice(index, office)}
													className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
												/>
												<span className="text-sm text-gray-700">{office}</span>
											</label>
										);
									})}
								</div>
								{entry.officeNames.length === 0 && (
									<div className="text-orange-600 text-xs mt-1 font-medium">
										Select at least one office, otherwise this footer is not used
									</div>
								)}
							</div>

							<div>
								<div className="flex justify-between items-center mb-2">
									<label className="text-gray-700 text-sm font-medium">Footer Lines</label>
									<button
										type="button"
										onClick={() => updateEntry(index, { footerLines: [...footerLines, ""] })}
										className="text-blue-600 hover:text-blue-800 text-sm font-medium"
									>
										+ Add Line
									</button>
								</div>
								{footerLines.map((line, lineIndex) => (
									<div key={lineIndex} className="flex gap-2 mb-2">
										<input
											type="text"
											value={line}
											onChange={(e) => updateLine(index, lineIndex, e.target.value)}
											className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
											placeholder={`Footer line ${lineIndex + 1}`}
										/>
										<button
											type="button"
											onClick={() =>
												updateEntry(index, { footerLines: footerLines.filter((_, i) => i !== lineIndex) })
											}
											className="px-3 py-2 text-red-600 hover:text-red-800"
										>
											Remove
										</button>
									</div>
								))}
								{footerLines.length === 0 && (
									<div className="text-gray-500 text-sm">No footer lines added, the default footer lines are used</div>
								)}
							</div>

							<ImageUploadField
								label="Footer Image"
								imagePath={entry.footerImageUrl}
								organizationId={organizationId}
								uploadDestination={`footer-office-${index}`}
								onImageChange={(path) => updateEntry(index, { footerImageUrl: path })}
								onImageRemove={() => updateEntry(index, { footerImageUrl: "" })}
								altText={`Office footer ${index + 1} image preview`}
							/>
						</div>
					);
				})
			)}
		</div>
	);
};

export default OfficeFooterManagement;
